import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import Swal from "sweetalert2";

import { usePreference } from "../../context/UserContext";
import QuizzesModel from "../../common/QuizzesModel";
import PayPalModal from "../paypal/PaypalModel";
import AdvertisementModel from "../../common/AdvertisementModel";
import ShowCart from "../ShowCart";

import DISCOURSEFORUM from "../../assets/img/df.png";
import IMG2 from "../../assets/img/Eureka Math _ LPS.webp";
import "./modulegrade.css";
import { Image } from "react-bootstrap";

/* ----------------------------------------
   Data (you can move to its own file later)
----------------------------------------- */
const bookData = [
  {
    title: "Learn, Practice, Succeed : Grade-1",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-1-users/9",
    learnItems: ["Lessons", "Coach","Additional Practice", "Quizzes", "Homework", "Spelling Games", "Discourse Forum"],
    practiceItems: ["Sprints & Fluency"],
    succeedItems: ["Additional Problems for Homework", "Homework Helpers"],
    description: "*The Learn and Practice books provide all of the printed materials a student uses for core instruction.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-2",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-2-users/10",
    learnItems: ["Lessons","Coach", "Additional Practice", "Quizzes", "Homework", "Spelling Games", "Discourse Forum"],
    practiceItems: ["Fluency with Decimals & Fractions"],
    succeedItems: ["Homework Support", "Practice Problems"],
    description: "*These books build foundational math for middle school.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-3",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-3-users/11",
    learnItems: ["Lessons", "Coach","Additional Practice", "Quizzes", "Homework", "Spelling Games", "Discourse Forum"],
    practiceItems: ["Word Problems Practice"],
    succeedItems: ["Extended Practice Tasks", "Concept Check Helpers"],
    description: "*Helps students advance problem-solving and algebraic thinking.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-4",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-4-users/12",
    learnItems: ["Lessons","Coach", "Additional Practice", "Quizzes", "Homework", "Spelling Games", "Discourse Forum"],
    practiceItems: ["Pre-Algebra Skill Building"],
    succeedItems: ["Challenge Problems", "Review Helpers"],
    description: "*Prepares students for high school algebra and geometry.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-5",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-5-users/13",
    learnItems: ["Lessons","Coach", "Additional Practice", "Quizzes", "Homework", "Games", "Discourse Forum"],
    practiceItems: ["Pre-Algebra Skill Building"],
    succeedItems: ["Challenge Problems", "Review Helpers"],
    description: "*Prepares students for high school algebra and geometry.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-6",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-6-users/14",
    learnItems: ["Lessons", "Coach","Additional Practice", "Quizzes", "Homework", "Games", "Discourse Forum"],
    practiceItems: ["Pre-Algebra Skill Building"],
    succeedItems: ["Challenge Problems", "Review Helpers"],
    description: "*Prepares students for high school algebra and geometry.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-7",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-7-users/15",
    learnItems: ["Lessons","Coach", "Additional Practice", "Quizzes", "Homework", "Games", "Discourse Forum"],
    practiceItems: ["Pre-Algebra Skill Building"],
    succeedItems: ["Challenge Problems", "Review Helpers"],
    description: "*Prepares students for high school algebra and geometry.",
    images: [IMG2],
  },
  {
    title: "Learn, Practice, Succeed : Grade-8",
    link: "https://myspelling-app.com/c/spelling-bee/spelling-grade-8-users/16",
    learnItems: ["Lessons","Coach", "Additional Practice", "Quizzes", "Homework", "Games", "Discourse Forum"],
    practiceItems: ["Pre-Algebra Skill Building"],
    succeedItems: ["Challenge Problems", "Review Helpers"],
    description: "*Prepares students for high school algebra and geometry.",
    images: [IMG2],
  },
];

// map grade index → quiz URL + note
const QUIZ_CONFIG = {
  "0": { url: "/playquiz/56", note: "(Dictionary 1 recommended)" },
  "1": { url: "/playquiz/56", note: "(Dictionary 1 recommended)" },
  "2": { url: "/playquiz/57", note: "(Dictionary 2 recommended)" },
  "3": { url: "/playquiz/57", note: "(Dict 3 recommended)" },
  "4": { url: "/playquiz/58", note: "(Dictionary 4 and 5 recommended)" },
  "5": { url: "/playquiz/58", note: "(Dictionary 4 and 5 recommended)" },
  "6": { url: "/playquiz/58", note: "(Dictionary 4 and 5 recommended)" },
  "7": { url: "/playquiz/58", note: "(Dictionary 4 and 5 recommended)" },
};

// icon map for items
const ICON_MAP = {
  "Lessons": "📘",
  "Additional Practice": "📝",
  "Quizzes": "🧪",
  "Homework": "🏠",
  "Spelling Games": "🎮",
  "Games": "🎲",
  "Discourse Forum": "💬",
  "Sprints & Fluency": "⚡",
  "Fluency with Decimals & Fractions": "➗",
  "Word Problems Practice": "❓",
  "Extended Practice Tasks": "📈",
  "Concept Check Helpers": "✅",
  "Pre-Algebra Skill Building": "➗",
  "Challenge Problems": "🏔️",
  "Review Helpers": "🔁",
};

// coach URL builder (opens new tab)
const getCoachUrl = (gradeIndex, itemLabel) =>
  `https://myspelling-app.com/coach?grade=${gradeIndex + 1}&item=${encodeURIComponent(itemLabel)}`;

// tiny localStorage helpers
const getLS = (key, fallback = null) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};
const setLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
};

/* ----------------------------------------
   Component
----------------------------------------- */
const ModuleGrade = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { subcategories = [] } = state || {};

  const {
    updateIsLoginOpen,
    updateIsSignUpOpen,
    updateIsNavVisible,
    showCart,
    updateShowCart,
    cart,
    updateCart,
  } = usePreference();

  const [openQuizModel, setOpenQuizModel] = useState(false);
  const [catId, setCatId] = useState(0); // (kept for compatibility with QuizzesModel)
  const [pack, setPack] = useState({});
  const [showPaypalModel, setShowPaypalModel] = useState(false);
  const [amount, setAmount] = useState(1);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const username = getLS("username", "");
  const isLoggedIn = Boolean(username);

  // nav visible
  useEffect(() => {
    updateIsNavVisible(true);
  }, [updateIsNavVisible]);

  // load saved cart
  useEffect(() => {
    const saved = (() => {
      try {
        const raw = localStorage.getItem("cart");
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    })();
    if (saved?.length) updateCart(saved);
  }, [updateCart]);

  // mirror cart to LS + toggle cart widget
  useEffect(() => {
    setLS("cart", cart);
    updateShowCart(cart.length > 0);
  }, [cart, updateShowCart]);

  // simulate loading (3 seconds)
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const filteredCart = useMemo(
    () =>
      cart.map((item) => ({
        packId: item.packId,
        price: item.price,
        isCollection: item.isCollection,
        type: item.type === "monthly",
      })),
    [cart]
  );

  const calculateTotal = useCallback(
    () => cart.reduce((sum, item) => sum + Number(item.price || 0), 0).toFixed(2),
    [cart]
  );

  const handleCheckout = useCallback(() => {
    const loggedin = localStorage.getItem("loggedin");
    if (!loggedin || loggedin === "false") {
      Swal.fire({
        icon: "warning",
        text: "Please login or signup to subscribe",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Login",
        denyButtonText: "Sign Up",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          updateIsLoginOpen(true);
          updateIsSignUpOpen(false);
        } else if (result.isDenied) {
          updateIsSignUpOpen(true);
          updateIsLoginOpen(false);
        }
      });
      return;
    }
    setAmount(calculateTotal());
    setShowPaypalModel(true);
  }, [calculateTotal, updateIsLoginOpen, updateIsSignUpOpen]);

  const deleteItem = useCallback(
    (packId, type) => {
      updateCart((prev) => prev.filter((i) => !(i.packId === packId && i.type === type)));
    },
    [updateCart]
  );

  const toggleCartVisibility = useCallback(() => {
    updateShowCart((v) => !v);
  }, [updateShowCart]);

  const addToCart = useCallback(
    (pack, type, price, isCollection, action, packname) => {
      updateShowCart(true);
      updateCart((prevCart) => {
        const current = Array.isArray(prevCart) ? prevCart : [];

        if (action === "remove") {
          return current.filter((i) => !(i.packId === pack.Id && i.isCollection === isCollection));
        }

        // action === "add"
        const already = current.some((i) => i.packId === pack.Id && i.isCollection === isCollection);
        if (already) {
          Swal.fire({ title: "Info", text: "This item is already in your cart.", icon: "info" });
          return current;
        }

        if (isCollection) {
          if (Array.isArray(pack.Categories)) {
            const categoryAlready = current.some((i) =>
              pack.Categories.some((c) => i.packId === c.CollectionId)
            );
            if (categoryAlready) {
              Swal.fire({
                title: "Alert",
                text:
                  "Some categories from this collection are already in your cart. " +
                  "Please remove them first if you wish to add the entire collection.",
                icon: "warning",
              });
              return current;
            }
            const withoutCats = current.filter(
              (i) => !pack.Categories.some((c) => i.packId === c.CollectionId)
            );
            return [
              ...withoutCats,
              { pack, packId: pack.Id, type, price, isCollection: true, packname },
            ];
          }
          return current; // defensive
        } else {
          const collectionInCart = current.some((i) => i.packId === pack.CollectionId);
          if (collectionInCart) {
            Swal.fire({
              title: "Alert",
              text:
                "The collection containing this category is already in your cart. " +
                "You cannot add individual categories from the collection.",
              icon: "warning",
            });
            return current;
          }
          return [...current, { pack, packId: pack.Id, type, price, isCollection: false, packname }];
        }
      });
    },
    [updateCart, updateShowCart]
  );

  const updateQuizId = useCallback((value) => navigate(`/playquiz/${value}`), [navigate]);

  /* -------------------------------
     Render a single "Learn" item
  -------------------------------- */
  const renderLearnItem = useCallback((item, gradeIndex, forumLink) => {
    const icon = ICON_MAP[item] ?? "📌";
    const coachHref = getCoachUrl(gradeIndex, item);

    // Coach pill shown on every item; opens new tab
    const CoachChip = (
      <a
        className="coach-label"
        href={coachHref}
        target="_blank"
        rel="noopener noreferrer"
        title="Open Coach"
        onClick={(e) => e.stopPropagation()}
      >
        🧑‍🏫 Coach
      </a>
    );

    if (item === "Spelling Games") {
      return (
        <>
          <span className="item-icon" aria-hidden>{icon}</span>
          <Link to="/fun">Spelling Games</Link>
        </>
      );
    }

    if(item === "Coach") {
      return (
        <>
        <a
        className="coach-label"
        href={coachHref}
        target="_blank"
        rel="noopener noreferrer"
        title="Open Coach"
        onClick={(e) => e.stopPropagation()}
      >
        🧑‍🏫 Coach <Image src="https://www.northsouth.org/wp-content/uploads/2024/09/nsf-logov1.png" alt="" style={{ marginLeft: 5, width:'50px',height:'20px' }}/>

      </a> 
        </>
      )
    }

    if (item === "Games") {
      return (
        <>
          <span className="item-icon" aria-hidden>{icon}</span>
          <Link to="/games" state={{ grade: `Grade-${gradeIndex + 1}` }}>
            Games
          </Link>
        </>
      );
    }

    if (item === "Quizzes") {
      const cfg = QUIZ_CONFIG[String(gradeIndex)];
      return (
        <>
          <span className="item-icon" aria-hidden>{icon}</span>
          <span>Quizzes:</span>
          <ul className="quiz-submenu" aria-label="Quiz options">
            <li>
              <Link to={cfg?.url ?? "/"}>Free Quiz {cfg?.note ?? ""}</Link>
            </li>
            <li>
              <Link to={{ pathname: "/", hash: "#notfreepack" }}>Paid Quiz</Link>
            </li>
          </ul>
        </>
      );
    }

    if (item === "Discourse Forum") {
      return (
        <>
          <span className="item-icon" aria-hidden>{icon}</span>
          <a href={forumLink} target="_blank" rel="noopener noreferrer" aria-label="Open Discourse Forum">
            Discourse Forum
            <img src={DISCOURSEFORUM} alt="" style={{ marginLeft: 10, width: 16 }} />
          </a>
        </>
      );
    }

    // default: Coming Soon items still get the coach chip
    return (
      <>
        <span className="item-icon" aria-hidden>{icon}</span>
        <span>{item}</span>
        <span className="blinking-text" aria-live="polite">Coming Soon</span>
      </>
    );
  }, []);

  // gated login
  if (!isLoggedIn) {
    return (
      <div className="container mt-5 text-center">
        <h6>Please login to view the page</h6>
        <button
          className="btn btn-primary mt-3"
          onClick={() => {
            navigate("/");
            updateIsLoginOpen(true);
          }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <>
      {showCart && (
        <ShowCart
          showCart={showCart}
          toggleCartVisibility={toggleCartVisibility}
          cart={cart}
          deleteItem={deleteItem}
          handleCheckout={handleCheckout}
          calculateTotal={calculateTotal}
          filteredCart={filteredCart}
        />
      )}

      {openQuizModel && (
        <QuizzesModel
          open={openQuizModel}
          updateQuizModel={setOpenQuizModel}
          catId={catId}
          updateQuizId={updateQuizId}
          pack={pack}
        />
      )}

      <PayPalModal
        showPaypalModel={showPaypalModel}
        updateShowPaypalModel={setShowPaypalModel}
        amount={amount}
        SelectedId={12}
        IsCollectionId={true}
        IsMonthly={true}
        Username={username}
        cartdata={cart}
        filteredCart={filteredCart}
      />

      {loading ? (
        <Backdrop
          sx={{
            color: "#000",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open
        >
          <div style={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#007bff" }} />
            <p style={{ marginTop: 8, fontWeight: 500 }}>Loading...</p>
          </div>
        </Backdrop>
      ) : (
        <div className="page-container">
          {bookData.map((b, i) => (
            <section key={b.title} className="section" aria-labelledby={`section-${i}`}>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <h3 id={`section-${i}`} className="section-title">{b.title}</h3>
              </div>

              <div className="content-grid">
                <ul className="list" aria-label={`${b.title} items`}>
                  {b.learnItems.map((item, idx) => (
                    <li key={`${item}-${idx}`}>{renderLearnItem(item, i, b.link)}</li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      )}

      <AdvertisementModel open={isOpenAdd} updateIsOpen={setIsOpenAdd} />
    </>
  );
};

export default ModuleGrade;
